import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import ShareBar from "@/components/feature/ShareBar";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { articles, getMovieById, getRelatedMovies } from "@/lib/media";
import { categories, typeLabels } from "@/mocks/site";
import EpisodeList from "./components/EpisodeList";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id ?? "");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSaved(false);
  }, [id]);

  useDocumentMeta({
    title: `${movie?.title ?? "影片"}${movie ? `（${movie.year}）` : ""} - 色虎影视详情`,
    description: movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.cast.slice(0, 3).join("、")}主演。${movie.synopsis.slice(0, 80)}`
      : "色虎影视详情页，查看影片简介、主创信息及相关推荐。",
    keywords: movie
      ? `${movie.title},${movie.title}详情,${movie.genres.join(",")},${movie.director},色虎影视`
      : "色虎影视详情,影片介绍,相关推荐",
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="portal-container py-16">
          <EmptyState
            icon="ri-film-line"
            title="这部影片不在演示数据中"
            description="可能是链接有误，或者该作品尚未收录。可以回到分类页继续浏览其他影片。"
            actionLabel="浏览电影分类"
            onAction={() => window.REACT_APP_NAVIGATE("/category/movie")}
          />
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const categoryMeta = categories.find((item) => item.key === movie.type);
  const relatedArticles = articles
    .filter((article) => article.tags.some((tag) => movie.tags.includes(tag) || movie.genres.includes(tag)))
    .slice(0, 3);

  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src={movie.poster}
            alt=""
            className="h-full w-full scale-110 object-cover object-top opacity-30 blur-md"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-900/70" />
        </div>

        <div className="portal-container relative py-6 md:py-10">
          <Breadcrumb
            tone="dark"
            items={[
              { label: categoryMeta?.name ?? "影视", to: `/category/${movie.type}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-full max-w-[200px] shrink-0 md:max-w-[240px]">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-primary-700">
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title} 影视详情 色虎`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
                {movie.title}
                <span className="ml-2 text-base font-normal text-primary-300 md:text-xl">（{movie.year}）</span>
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-primary-200 md:text-sm">
                <span className="flex items-center gap-1.5 rounded bg-accent-500 px-2 py-1 font-heading text-sm font-bold text-primary-950">
                  <i className="ri-star-fill text-xs" aria-hidden="true" />
                  {movie.rating.toFixed(1)}
                </span>
                <span>{typeLabels[movie.type]}</span>
                <span className="text-primary-600">|</span>
                <span>{movie.region}</span>
                <span className="text-primary-600">|</span>
                <span>{movie.duration}</span>
                <span className="text-primary-600">|</span>
                <span>{movie.status}</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="rounded-md border border-primary-700 px-2 py-0.5 text-[11px] text-primary-200 transition-colors hover:border-accent-400 hover:text-accent-300"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <dl className="mt-5 grid gap-2 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-primary-400">导演</dt>
                  <dd className="text-background-100">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-primary-400">主演</dt>
                  <dd className="text-background-100">{movie.cast.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-primary-400">热度</dt>
                  <dd className="text-background-100">{movie.heat.toLocaleString()} 次讨论</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-primary-400">更新</dt>
                  <dd className="text-background-100">{movie.updatedAt}</dd>
                </div>
              </dl>

              <p className="mt-5 max-w-3xl text-sm leading-7 text-primary-100">{movie.synopsis}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSaved((value) => !value)}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                    saved
                      ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                      : "border border-primary-500 bg-primary-800/60 text-background-50 hover:border-accent-400 hover:text-accent-300"
                  }`}
                >
                  <i className={saved ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true" />
                  {saved ? "已加入片单" : "加入片单"}
                </button>
                <Link
                  to={`/category/${movie.type}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-500 bg-primary-800/60 px-4 py-2 text-sm text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-grid-line" aria-hidden="true" />
                  同类推荐
                </Link>
              </div>
              <p className="mt-2 text-[11px] text-primary-400">
                <i className="ri-information-line mr-1" aria-hidden="true" />
                片单仅保存在当前页面，本站为演示站点，不提供影片播放服务。
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="portal-container mt-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-3">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-file-text-line mr-1.5 text-accent-500" aria-hidden="true" />
                  剧情简介
                </h2>
                <ShareBar title={`${movie.title}（${movie.year}） - 色虎影视详情`} />
              </div>
              <p className="mt-4 text-sm leading-8 text-foreground-700">{movie.synopsis}</p>
              <p className="mt-3 text-sm leading-8 text-foreground-600">
                本页信息包含影片的基本资料、主创阵容与剧集目录，所有内容与分类页、搜索结果保持一致。
                {movie.episodes.length > 0
                  ? " 剧集目录中的每一集都可以点击切换，查看对应的剧情梗概。"
                  : " 该作品为单片形式，暂无剧集目录，可在下方查看同类型作品推荐。"}
              </p>

              <div className="mt-5 grid gap-3 rounded-md bg-background-100 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] text-foreground-400">影片标签</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {movie.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/search?q=${encodeURIComponent(tag)}`}
                        className="rounded-md bg-background-50 px-2 py-1 text-xs text-foreground-600 transition-colors hover:text-accent-600"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-400">快速导航</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                    <Link to="/news" className="text-primary-700 hover:text-accent-600">
                      相关影视资讯
                    </Link>
                    <Link to={`/category/${movie.type}`} className="text-primary-700 hover:text-accent-600">
                      更多{typeLabels[movie.type]}
                    </Link>
                    <Link
                      to={`/search?q=${encodeURIComponent(movie.director)}`}
                      className="text-primary-700 hover:text-accent-600"
                    >
                      搜索导演作品
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            <EpisodeList episodes={movie.episodes} totalEpisodes={movie.totalEpisodes} status={movie.status} />

            <section className="mt-8" aria-label="相关推荐">
              <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                  相关推荐
                </h2>
                <Link to={`/category/${movie.type}`} className="text-xs font-medium text-primary-700 hover:text-accent-600">
                  查看全部
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <section className="card-surface p-4">
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                <i className="ri-information-line text-primary-600" aria-hidden="true" />
                影片信息
              </h2>
              <dl className="mt-3 space-y-2.5 text-xs">
                {[
                  { label: "片名", value: movie.title },
                  { label: "年份", value: `${movie.year}` },
                  { label: "分类", value: typeLabels[movie.type] },
                  { label: "地区", value: movie.region },
                  { label: "类型", value: movie.genres.join(" / ") },
                  { label: "片长", value: movie.duration },
                  { label: "状态", value: movie.status },
                  { label: "导演", value: movie.director },
                  { label: "评分", value: `${movie.rating.toFixed(1)} 分` },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3 border-b border-background-100 pb-2 last:border-b-0 last:pb-0">
                    <dt className="w-10 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="text-foreground-700">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="card-surface p-4">
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                <i className="ri-newspaper-line text-accent-500" aria-hidden="true" />
                相关资讯
              </h2>
              <div className="mt-1">
                {(relatedArticles.length > 0 ? relatedArticles : articles.slice(0, 3)).map((article) => (
                  <Link
                    key={article.id}
                    to={`/news/detail/${article.id}`}
                    className="group block border-b border-background-100 py-3 last:border-b-0"
                  >
                    <p className="line-clamp-2 text-sm leading-6 text-foreground-800 transition-colors group-hover:text-accent-600">
                      {article.title}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-400">
                      {article.category} · {article.date}
                    </p>
                  </Link>
                ))}
              </div>
              <Link to="/news" className="mt-3 flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-accent-600">
                更多影视资讯
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </section>

            <section className="rounded-lg bg-primary-500 p-5 text-background-50">
              <h2 className="font-heading text-base font-bold">找片小提示</h2>
              <p className="mt-2 text-xs leading-6 text-primary-100">
                使用顶部搜索框输入片名、导演或演员，即可同时检索影视作品与影视资讯。
              </p>
              <Link
                to={`/search?q=${encodeURIComponent(movie.genres[0])}`}
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                搜索「{movie.genres[0]}」
                <i className="ri-search-line" aria-hidden="true" />
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}